import { useDraggable, useDroppable } from "@dnd-kit/core"
import {
  ArrowDownTrayIcon,
  PaintBrushIcon,
  PhotoIcon,
  PlayIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { isEnhancedAsset, type ScrapedImage } from "../types"
import CardOverlayPreview from "./overlay/CardOverlayPreview"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export const SLOT_DROPPABLE_PREFIX = "slot:"
export const SLOT_ITEM_PREFIX = "slot-asset:"

export const CAROUSEL_SLOT_COUNT = 10

interface AdCardSlotProps {
  index: number
  asset: ScrapedImage | undefined
  template: "single" | "carousel"
  isDemoEnabled: boolean
  isAcceptingDrop: boolean
  /** Carousel-only: highlights the slot whose placements are shown on the
   *  right-hand preview, and clicking a filled slot makes it active. */
  isActiveForPreview?: boolean
  /** Show a loading overlay while the asset is being upscaled by the
   *  low-res banner's batch-enhance flow. The original image stays
   *  visible underneath so the user sees what's being replaced. */
  isEnhancing?: boolean
  /** Show a lightweight loading spinner while this asset's saved AI
   *  variants (placements) are being fetched by the bulk prefill lookup. */
  isLoadingPlacements?: boolean
  onClearSlot: (slotIndex: number) => void
  /** Selects this asset as the source for the always-visible Create with AI
   *  panel above the grid (Image → Video for images, Slideshow for videos). */
  onOpenAi?: (asset: ScrapedImage) => void
  /** Opens the Brand overlay flow with this asset as the media — pick a
   *  saved layout, fine-tune, bake it onto the image/video. */
  onOpenOverlay?: (asset: ScrapedImage) => void
  /** Download this asset with its assigned layout baked in (no upload). */
  onDownloadOverlay?: (asset: ScrapedImage) => void
  /** Video cards only: open the frame picker to choose the video's thumbnail. */
  onPickThumbnail?: (asset: ScrapedImage) => void
  onSelectForPreview?: (slotIndex: number) => void
}

function AdCardSlot({
  index,
  asset,
  template,
  isDemoEnabled,
  isAcceptingDrop,
  isActiveForPreview,
  isEnhancing,
  isLoadingPlacements,
  onClearSlot,
  onOpenAi,
  onOpenOverlay,
  onDownloadOverlay,
  onPickThumbnail,
  onSelectForPreview,
}: AdCardSlotProps) {
  const droppable = useDroppable({
    id: `${SLOT_DROPPABLE_PREFIX}${index}`,
    data: { kind: "slot", index },
  })
  const draggable = useDraggable({
    id: `${SLOT_ITEM_PREFIX}${index}`,
    data: { kind: "slot", index, assetId: asset?.id },
    disabled: !asset,
  })

  const isCarousel = template === "carousel"
  const thumb = asset?.thumbnailUrl || asset?.url
  // "Low res" means the smaller axis is under Meta's 1080px recommendation.
  // Earlier this checked `width < 1080 || height < 1080`, which flagged any
  // standard landscape creative (1200×628, 1920×1080-cropped etc.) since
  // their shorter side is naturally under 1080. Using `Math.min` keeps the
  // warning meaningful — it only fires when the image truly couldn't fill
  // a 1080-square placement without upscaling.
  // Already-enhanced assets are the best output we can produce — don't nag to
  // re-enhance them even if Imagen's upscale landed just under 1080 on an axis.
  const isLowRes =
    asset?.type !== "video" &&
    asset?.width != null &&
    asset?.height != null &&
    Math.min(asset.width, asset.height) < 1080 &&
    !isEnhancedAsset(asset)
  const isUnknownDims =
    asset?.type !== "video" && (asset?.width == null || asset?.height == null)

  const hasNativeDims =
    asset?.type !== "video" &&
    asset?.width != null &&
    asset?.height != null &&
    asset.width > 0 &&
    asset.height > 0
  const dynamicAspect = hasNativeDims
    ? { aspectRatio: `${asset!.width} / ${asset!.height}` }
    : undefined

  return (
    <div
      ref={droppable.setNodeRef}
      onClick={
        isCarousel && asset && onSelectForPreview
          ? () => onSelectForPreview(index)
          : undefined
      }
      style={dynamicAspect}
      className={cn(
        "relative rounded-xl border-2 transition-all overflow-hidden",
        !hasNativeDims &&
          (isCarousel
            ? "aspect-square"
            : "aspect-[4/5] max-w-sm mx-auto w-full"),
        !isCarousel && "max-w-sm mx-auto w-full",
        // Active carousel card (the one whose placements are previewed) gets
        // a brand ring; filled but inactive cards stay neutral. Empty slots
        // keep their dashed drop affordance.
        asset
          ? isActiveForPreview
            ? "border-brand ring-2 ring-brand/40 bg-system-gray-900/40"
            : "border-brand/40 bg-system-gray-900/40"
          : isAcceptingDrop
            ? "border-brand border-solid bg-brand/10"
            : droppable.isOver
              ? "border-brand border-dashed bg-brand/5"
              : "border-system-gray-700/50 border-dashed bg-system-gray-900/30",
        isCarousel && asset && onSelectForPreview && "cursor-pointer",
      )}
    >
      {/* Slot label badge — always visible so empty slots are numbered */}
      <div className="absolute top-2 left-2 z-10 bg-black/60 rounded px-1.5 py-0.5 pointer-events-none">
        <span className="text-[10px] text-white font-bold">
          {isCarousel ? `Card ${index + 1}` : "Asset"}
        </span>
      </div>

      {asset ? (
        <div
          ref={draggable.setNodeRef}
          {...draggable.listeners}
          {...draggable.attributes}
          className={cn(
            "group w-full h-full cursor-grab active:cursor-grabbing select-none touch-none",
            draggable.isDragging && "opacity-30",
          )}
        >
          <div
            className={cn(
              "w-full h-full bg-system-gray-900",
              isDemoEnabled && "blur-md",
            )}
          >
            {asset.type === "video" ? (
              // No autoplay — the slot shows the poster/first frame. Users
              // hit the centered play overlay to open the preview modal.
              // `#t=0.1` forces the first-frame paint when there's no poster
              // (Safari otherwise renders nothing), and a `thumbnailUrl`
              // that's just the video URL backfilled by a converter is not
              // a poster — an <img/poster> load of a video file errors.
              <video
                src={`${asset.url}#t=0.1`}
                poster={
                  asset.thumbnailUrl && asset.thumbnailUrl !== asset.url
                    ? asset.thumbnailUrl
                    : undefined
                }
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : thumb ? (
              <img
                src={thumb}
                alt=""
                className="w-full h-full object-cover pointer-events-none"
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[10px] text-system-gray-500">
                no preview
              </div>
            )}
          </div>

          {/* Assigned layout — live, non-destructive preview over the media.
              Baked in only when the ad is created (save draft). */}
          {asset.overlayDoc && <CardOverlayPreview doc={asset.overlayDoc} />}

          {asset.type === "video" && (
            <div
              aria-hidden
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="w-10 h-10 rounded-full bg-black/55 flex items-center justify-center">
                <PlayIcon className="w-5 h-5 text-white/90" />
              </div>
            </div>
          )}

          {/* Video thumbnail picker — choose the frame that fronts the video
              (becomes the thumbnail Meta receives on blast). Videos without
              a library id are resolved by URL in the click handler, which
              explains itself when the video isn't in the library at all. */}
          {onPickThumbnail && asset.type === "video" && (
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation()
                onPickThumbnail(asset)
              }}
              title="Pick a thumbnail frame from this video"
              aria-label="Pick thumbnail frame"
              className="absolute top-2 right-10 w-6 h-6 rounded-full flex items-center justify-center bg-black/70 hover:bg-brand text-white transition opacity-0 group-hover:opacity-100 z-10"
            >
              <PhotoIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Remove (clear slot — asset stays in pool) */}
          <button
            onPointerDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation()
              onClearSlot(index)
            }}
            title="Clear this slot (asset stays in Selected Assets)"
            aria-label="Clear slot"
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center bg-black/70 hover:bg-red-500 text-white transition opacity-0 group-hover:opacity-100 z-10"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>

          {/* AI actions — sends the asset to the Create with AI panel as its
              source (image → video, or a slideshow slide). */}
          {onOpenAi && (
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation()
                onOpenAi(asset)
              }}
              title="Create with AI — turn this asset into a video or slideshow"
              aria-label="Create with AI"
              className="absolute bottom-2 right-2 w-7 h-7 rounded-full flex items-center justify-center bg-brand/85 text-white hover:bg-brand transition opacity-0 group-hover:opacity-100 z-10"
            >
              <SparklesIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Brand overlay — apply a saved layout over this image/video and
              bake it back into the card. */}
          {onOpenOverlay && (
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation()
                onOpenOverlay(asset)
              }}
              title="Apply a brand overlay layout to this asset"
              aria-label="Apply brand overlay"
              className="absolute bottom-2 right-10 w-7 h-7 rounded-full flex items-center justify-center bg-system-gray-800/90 text-white hover:bg-brand transition opacity-0 group-hover:opacity-100 z-10"
            >
              <PaintBrushIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Download the media WITH its assigned layout baked in — renders
              client-side, nothing is uploaded or saved. */}
          {onDownloadOverlay && asset.overlayDoc && (
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation()
                onDownloadOverlay(asset)
              }}
              title="Download with the layout baked in"
              aria-label="Download with layout"
              className="absolute bottom-2 right-[4.5rem] w-7 h-7 rounded-full flex items-center justify-center bg-system-gray-800/90 text-white hover:bg-brand transition opacity-0 group-hover:opacity-100 z-10"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Dimension badge */}
          {asset.type !== "video" &&
            isLowRes &&
            asset.width &&
            asset.height && (
              <div
                className="absolute bottom-2 left-2 bg-system-error-500/90 rounded px-1.5 py-0.5 pointer-events-none z-10"
                title={`${asset.width}x${asset.height}px — Meta recommends 1080px minimum`}
              >
                <span className="text-[9px] text-white font-bold">
                  {asset.width}x{asset.height}
                </span>
              </div>
            )}
          {asset.type !== "video" && !isLowRes && isUnknownDims && (
            <div
              className="absolute bottom-2 left-2 bg-system-gray-600/90 rounded px-1.5 py-0.5 pointer-events-none z-10"
              title="Image dimensions unknown"
            >
              <span className="text-[9px] text-white font-bold">? px</span>
            </div>
          )}

          {/* Enhancing overlay — sits on top of the original image while
              the low-res banner's batch flow upscales it. Kept pointer-
              events-none so it doesn't fight the drag handle underneath. */}
          {isEnhancing && (
            <div
              aria-live="polite"
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm z-20 pointer-events-none"
            >
              <span className="loading loading-spinner loading-md text-brand" />
              <span className="text-[10px] uppercase tracking-wide text-white font-semibold">
                Enhancing…
              </span>
            </div>
          )}

          {/* Placement-prefill spinner — a lightweight badge (not a full
              overlay like Enhancing) shown while the bulk variant lookup
              resolves this asset's saved placements. Suppressed while
              enhancing so the two indicators never stack. */}
          {isLoadingPlacements && !isEnhancing && (
            <div
              aria-live="polite"
              className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-black/70 rounded-full px-2 py-0.5 pointer-events-none"
            >
              <span className="loading loading-spinner loading-xs text-brand" />
              <span className="text-[9px] text-white font-semibold">
                Loading…
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          className="w-full h-full flex flex-col items-center justify-center gap-2 text-system-gray-500"
          title="Drag an asset from the Selected Assets drawer"
        >
          <PhotoIcon className="w-8 h-8 opacity-50" />
          <span className="text-[11px] font-medium text-center px-2">
            {isAcceptingDrop ? "Drop here" : "Drag asset here"}
          </span>
        </div>
      )}
    </div>
  )
}

interface AdCardSlotsProps {
  template: "single" | "carousel"
  adAssets: ScrapedImage[]
  isDemoEnabled: boolean
  draggingFromPool: boolean
  /** Carousel-only: index of the card whose placements are shown on the
   *  right-hand preview. The active card gets a brand ring. */
  activeCarouselIndex?: number
  /** Asset ids currently being upscaled by the low-res banner's batch
   *  enhance flow. Slots whose asset id is in this set render a loading
   *  overlay until the asset is swapped for its enhanced replacement. */
  enhancingAssetIds?: ReadonlySet<string>
  /** Library `assetId`s whose saved AI variants are currently being fetched by
   *  the bulk prefill lookup. Cards whose `assetId` is in this set show a
   *  lightweight loading spinner until their placements resolve. */
  loadingAssetIds?: ReadonlySet<string>
  onClearSlot: (slotIndex: number) => void
  onOpenAi?: (asset: ScrapedImage) => void
  /** Opens the Brand overlay flow with this card's asset as the media. */
  onOpenOverlay?: (asset: ScrapedImage) => void
  /** Download a card's media with its assigned layout baked in. */
  onDownloadOverlay?: (asset: ScrapedImage) => void
  /** Video cards only: open the frame picker to choose the video's thumbnail. */
  onPickThumbnail?: (asset: ScrapedImage) => void
  onSelectCarouselCard?: (slotIndex: number) => void
}

export default function AdCardSlots({
  template,
  adAssets,
  isDemoEnabled,
  draggingFromPool,
  activeCarouselIndex,
  enhancingAssetIds,
  loadingAssetIds,
  onClearSlot,
  onOpenAi,
  onOpenOverlay,
  onDownloadOverlay,
  onPickThumbnail,
  onSelectCarouselCard,
}: AdCardSlotsProps) {
  const total = template === "single" ? 1 : CAROUSEL_SLOT_COUNT
  // For single: only the one slot accepts pool drops.
  // For carousel: the first empty slot accepts new drops (packed-array model).
  // Filled slots always accept drops (swap/replace).
  const nextEmptyIndex = adAssets.length
  return (
    <div
      className={cn(
        template === "single"
          ? "flex justify-center"
          : "grid grid-cols-5 gap-3",
      )}
    >
      {Array.from({ length: total }).map((_, idx) => {
        const asset = adAssets[idx]
        const isFilled = !!asset
        const isAcceptingDrop =
          draggingFromPool && (isFilled || idx === nextEmptyIndex)
        return (
          <AdCardSlot
            key={idx}
            index={idx}
            asset={asset}
            template={template}
            isDemoEnabled={isDemoEnabled}
            isAcceptingDrop={isAcceptingDrop}
            isActiveForPreview={
              template === "carousel" && idx === activeCarouselIndex
            }
            isEnhancing={!!asset && !!enhancingAssetIds?.has(asset.id)}
            isLoadingPlacements={
              !!asset?.assetId && !!loadingAssetIds?.has(asset.assetId)
            }
            onClearSlot={onClearSlot}
            onOpenAi={onOpenAi}
            onOpenOverlay={onOpenOverlay}
            onDownloadOverlay={onDownloadOverlay}
            onPickThumbnail={onPickThumbnail}
            onSelectForPreview={onSelectCarouselCard}
          />
        )
      })}
    </div>
  )
}
